import React, { useCallback, useEffect, useState } from "react";

import { Alert, Skeleton, Stack, Typography } from "@mui/material";

import { ApplicationEndpoints } from "@generated/application";
import { useApi } from "@platform/hooks/useApi";

import { TemplateListItem } from "../../pages/CreateStackPage/TemplateCard";
import { type StackTemplate, type StackType } from "../../types";

export interface TemplateSelectorProps {
  stackType: StackType;
  cloudProvider: string;
  value: StackTemplate | null;
  onChange: (template: StackTemplate | null) => void;
}

export function TemplateSelector({
  stackType,
  cloudProvider,
  value,
  onChange,
}: TemplateSelectorProps): React.ReactElement {
  const { get } = useApi();
  const [templates, setTemplates] = useState<StackTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await get<StackTemplate[]>(
        `${ApplicationEndpoints.LIST_STACK_TEMPLATES}?systemType=${encodeURIComponent(stackType)}`,
        { errorMessage: "Failed to load templates" },
      );
      setTemplates(Array.isArray(result) ? result : []);
    } catch {
      setError("Failed to load templates.");
    } finally {
      setLoading(false);
    }
  }, [get, stackType]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <Stack spacing={1}>
        {[1, 2, 3].map((i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            height={48}
            sx={{ borderRadius: 1 }}
          />
        ))}
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (templates.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No templates available for this stack type.
      </Typography>
    );
  }

  return (
    <Stack spacing={0.5}>
      {templates.map((tmpl) => (
        <TemplateListItem
          key={tmpl.slug}
          template={tmpl}
          cloudProvider={cloudProvider}
          selected={value?.slug === tmpl.slug}
          onSelect={(t) => onChange(t)}
          onExpand={(t) =>
            setExpandedSlug((prev) => (prev === t.slug ? null : t.slug))
          }
          expanded={expandedSlug === tmpl.slug}
        />
      ))}
    </Stack>
  );
}
